import React, { useState } from "react";
import WeatherCard from "./components/WeatherCard";
import ForecastCard from "./components/ForecastCard";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./App.css";

const API_KEY = "eb704ded5c529e736ef1153e748d3c55"; // ✅ Your actual OpenWeatherMap key

function App() {
  const [city, setCity] = useState("");
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [hourly, setHourly] = useState([]);
  const [error, setError] = useState("");

  // ✅ Fetch weather by city name
  const fetchWeather = async () => {
    if (!city) return;
    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/weather?q=${city}&appid=${API_KEY}&units=metric`
      );
      const data = await response.json();
      console.log("Current Weather:", data);

      if (data.cod === 200) {
        setWeather(data);
        setError("");
        fetchForecast(data.coord.lat, data.coord.lon);
      } else {
        setError("City not found!");
        setWeather(null);
      }
    } catch (err) {
      console.error("Error fetching weather:", err);
      setError("Error fetching weather data!");
      setWeather(null);
    }
  };

  // ✅ Fetch weather using current location
  const fetchByLocation = () => {
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const response = await fetch(
            `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&appid=${API_KEY}&units=metric`
          );
          const data = await response.json();
          console.log("Location Weather:", data);

          setWeather(data);
          setError("");
          fetchForecast(latitude, longitude);
        } catch (err) {
          console.error("Error fetching location weather:", err);
          setError("Unable to get location data!");
        }
      },
      () => setError("Unable to access location!")
    );
  };

  // ✅ Fetch 5-day & hourly forecast
  const fetchForecast = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`
      );
      const data = await response.json();
      console.log("Forecast Data:", data);

      if (!Array.isArray(data.list)) {
        setForecast([]);
        return;
      }

      // Extract next 5 days (every 8th record = 24 hours)
      const daily = data.list.filter((_, index) => index % 8 === 0);
      setForecast(daily);

      // Extract next 12 hours for hourly temperature chart
      const next12 = data.list.slice(0, 12).map((item) => ({
        time: item.dt_txt.split(" ")[1].slice(0, 5),
        temp: item.main.temp,
      }));
      setHourly(next12);
    } catch (err) {
      console.error("Error fetching forecast:", err);
    }
  };

  return (
    <div className="app-container">
      <h1>🌤️ Weather Forecast</h1>

      <div className="search-container">
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="Enter city name"
        />
        <button onClick={fetchWeather}>Search</button>
        <button onClick={fetchByLocation}>📍 Use My Location</button>
      </div>

      {error && <p className="error">{error}</p>}
      {weather && <WeatherCard weather={weather} />}

      {forecast.length > 0 && (
        <>
          <h2>5-Day Forecast</h2>
          <div className="forecast-container">
            {forecast.map((day, index) => (
              <ForecastCard key={index} day={day} />
            ))}
          </div>

          <h2>Hourly Temperature</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={350}>
              <LineChart
                data={hourly}
                margin={{ top: 30, right: 30, left: 0, bottom: 10 }}
              >
                <defs>
                  <linearGradient id="colorTemp" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1E90FF" stopOpacity={0.8} />
                    <stop offset="100%" stopColor="#87CEFA" stopOpacity={0.1} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="time"
                  tick={{ fill: "#555", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: "#555", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  domain={["dataMin - 2", "dataMax + 2"]}
                  label={{
                    value: "°C",
                    angle: -90,
                    position: "insideLeft",
                    fill: "#666",
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#ffffffdd",
                    borderRadius: "10px",
                    border: "1px solid #ccc",
                  }}
                  labelStyle={{ color: "#333", fontWeight: "bold" }}
                  itemStyle={{ color: "#1E90FF" }}
                />
                <Line
                  type="monotone"
                  dataKey="temp"
                  stroke="url(#colorTemp)"
                  strokeWidth={3}
                  dot={{ r: 5, fill: "#1E90FF", strokeWidth: 2, stroke: "#fff" }}
                  activeDot={{
                    r: 7,
                    stroke: "#1E90FF",
                    strokeWidth: 2,
                    fill: "#fff",
                  }}
                  isAnimationActive={true}
                  animationDuration={1200}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}

export default App;
